import React, { useEffect, useRef, useState } from "react";

// 3rd Party Library
import PropTypes from "prop-types";
import { FaClock } from "react-icons/fa";

// Styles
import styles from "./style.module.scss";

const TimePicker = ({ initialTime = "00:00", onChange, value }) => {
  const [hour, setHour] = useState(initialTime.split(":")[0]);
  const [minute, setMinute] = useState(initialTime.split(":")[1]);
  const [showTimePicker, setShowTimePicker] = useState(false);
  const pickerRef = useRef(null);

  const padZero = (num) => num.toString().padStart(2, "0");

  const updateTime = (newHour, newMinute) => {
    const formattedHour = padZero(newHour);
    const formattedMinute = padZero(newMinute);
    const formattedTime = `${formattedHour}:${formattedMinute}`;
    setHour(formattedHour);
    setMinute(formattedMinute);
    if (onChange) {
      onChange(formattedTime);
    }
  };

  const handleHourChange = (e) => {
    let newHour = e.target.value.replace(/^0+/, "") || "0";
    const parsed = parseInt(newHour, 10);
    if (!isNaN(parsed) && parsed >= 0 && parsed <= 23) {
      updateTime(parsed, minute);
    }
  };

  const handleMinuteChange = (e) => {
    let newMinute = e.target.value.replace(/^0+/, "") || "0";
    const parsed = parseInt(newMinute, 10);
    if (!isNaN(parsed) && parsed >= 0 && parsed <= 59) {
      updateTime(hour, parsed);
    }
  };

  const incrementHour = () => updateTime((parseInt(hour, 10) + 1) % 24, minute);

  const decrementHour = () =>
    updateTime((parseInt(hour, 10) - 1 + 24) % 24, minute);

  const incrementMinute = () => {
    const newMinute = (parseInt(minute, 10) + 1) % 60;
    let newHour = parseInt(hour, 10);
    if (newMinute === 0) {
      newHour = (newHour + 1) % 24;
    }
    updateTime(newHour, newMinute);
  };

  const decrementMinute = () => {
    let newMinute = parseInt(minute, 10) - 1;
    let newHour = parseInt(hour, 10);
    if (newMinute < 0) {
      newMinute = 59;
      newHour = (newHour - 1 + 24) % 24;
    }
    updateTime(newHour, newMinute);
  };

  const handleInputClick = () => {
    if (value) {
      const [valHour, valMinute] = value.split(":");
      if (valHour !== hour || valMinute !== minute) {
        updateTime(valHour, valMinute);
      }
    }
    setShowTimePicker(!showTimePicker);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setShowTimePicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={pickerRef}>
      <div
        onClick={handleInputClick}
        style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
      >
        <FaClock className={styles["rmdb-time-icon"]} />
        <input
          type="text"
          readOnly
          className={styles["focus-input"]}
          value={value || `${hour}:${minute}`}
        />
      </div>

      {showTimePicker && (
        <div className={styles["rmdp-wrapper3"]}>
          <div
            className={`${styles["rmdp-time-picker"]} ${styles["rmdp-shadow"]}`}
          >
            {/* Hour Picker */}
            <div>
              <button
                type="button"
                className={`${styles["rmdp-arrow-container"]} ${styles["rmdp-up"]}`}
                onClick={incrementHour}
              >
                <i className={styles["rmdp-arrow"]}></i>
              </button>
              <input
                type="text"
                value={hour}
                onChange={handleHourChange}
                className={styles["time-input"]}
              />
              <button
                type="button"
                className={`${styles["rmdp-arrow-container"]} ${styles["rmdp-down"]}`}
                onClick={decrementHour}
              >
                <i className={styles["rmdp-arrow"]}></i>
              </button>
            </div>

            <span className={styles.dvdr}>:</span>

            {/* Minute Picker */}
            <div>
              <button
                type="button"
                className={`${styles["rmdp-arrow-container"]} ${styles["rmdp-up"]}`}
                onClick={incrementMinute}
              >
                <i className={styles["rmdp-arrow"]}></i>
              </button>
              <input
                type="text"
                value={minute}
                onChange={handleMinuteChange}
                className={styles["time-input"]}
              />
              <button
                type="button"
                className={`${styles["rmdp-arrow-container"]} ${styles["rmdp-down"]}`}
                onClick={decrementMinute}
              >
                <i className={styles["rmdp-arrow"]}></i>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

TimePicker.propTypes = {
  initialTime: PropTypes.string,
  onChange: PropTypes.func,
  value: PropTypes.string,
};

export default React.memo(TimePicker);



